import { io, Socket } from 'socket.io-client'
import type {
  IncomingCallEvent,
  CallAcceptedEvent,
  CallDeclinedEvent,
  CallEndedEvent,
  OfferEvent,
  AnswerEvent,
  IceCandidateEvent,
  BreakthroughCallEvent,
  PatientAvailabilityEvent,
} from '@virtual-care/shared'
import type { SignalingServiceInterface } from '@virtual-care/shared'
import { requireEnv } from '@virtual-care/shared'

export class SignalingService implements SignalingServiceInterface {
  private socket: Socket | null = null
  private callbacks: Map<string, Set<Function>> = new Map()
  private socketUrl: string

  constructor() {
    this.socketUrl = requireEnv(process.env.VUE_APP_SOCKET_URL, "VUE_APP_SOCKET_URL")
    console.log('[SignalingService] Constructor called, socketUrl:', this.socketUrl)
  }

  connect(roomId: string): void {
    // If socket is already connected, just re-register the room
    if (this.socket?.connected) {
      console.log('[SignalingService] Socket already connected, re-registering room:', roomId)
      this.socket.emit('register_room', roomId)
      return
    }

    console.log('[SignalingService] Creating new socket connection for room:', roomId)
    this.socket = io(this.socketUrl, {
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 5,
    })

    this.socket.on('connect', () => {
      console.log('[SignalingService] Socket connected:', this.socket?.id)
      console.log('[SignalingService] Registering room:', roomId)
      this.socket?.emit('register_room', roomId)
    })

    this.socket.on('disconnect', () => {
      console.warn('[SignalingService] Socket disconnected')
    })

    this.setupEventHandlers()
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect()
      this.socket = null
    }
  }

  private setupEventHandlers(): void {
    if (!this.socket) return

    this.socket.on('incoming_call', (event: IncomingCallEvent) => {
      this.emit('incoming_call', event)
    })

    this.socket.on('breakthrough_call', (event: BreakthroughCallEvent) => {
      this.emit('breakthrough_call', event)
    })

    this.socket.on('call_accepted', (event: CallAcceptedEvent) => {
      this.emit('call_accepted', event)
    })

    this.socket.on('call_declined', (event: CallDeclinedEvent) => {
      this.emit('call_declined', event)
    })

    this.socket.on('call_ended', (event: CallEndedEvent) => {
      this.emit('call_ended', event)
    })

    this.socket.on('patient_availability', (event: PatientAvailabilityEvent) => {
      this.emit('patient_availability', event)
    })

    // WebRTC signaling – only exercised when VIDEO_PROVIDER=vanilla-webrtc
    this.socket.on('offer', (event: OfferEvent) => {
      this.emit('offer', event)
    })

    this.socket.on('answer', (event: AnswerEvent) => {
      this.emit('answer', event)
    })

    this.socket.on('ice-candidate', (event: IceCandidateEvent) => {
      this.emit('ice-candidate', event)
    })
  }

  on(event: string, callback: Function): void {
    if (!this.callbacks.has(event)) {
      this.callbacks.set(event, new Set())
    }
    this.callbacks.get(event)!.add(callback)
  }

  off(event: string, callback?: Function): void {
    if (!callback) {
      this.callbacks.delete(event)
      return
    }
    this.callbacks.get(event)?.delete(callback)
  }

  private emit(event: string, data: unknown): void {
    const callbacks = this.callbacks.get(event)
    if (callbacks) {
      callbacks.forEach(cb => cb(data))
    }
  }

  // WebRTC signaling methods (used only by vanilla-webrtc path in useVideoClient)
  sendOffer(sessionId: string, offer: RTCSessionDescriptionInit, from: string): void {
    this.socket?.emit('offer', { type: 'offer', sessionId, offer, from } satisfies OfferEvent)
  }

  sendAnswer(sessionId: string, answer: RTCSessionDescriptionInit, from: string): void {
    this.socket?.emit('answer', { type: 'answer', sessionId, answer, from } satisfies AnswerEvent)
  }

  sendIceCandidate(sessionId: string, candidate: RTCIceCandidateInit, from: string): void {
    this.socket?.emit('ice-candidate', { type: 'ice-candidate', sessionId, candidate, from } satisfies IceCandidateEvent)
  }

  joinRoom(roomId: string): void {
    this.socket?.emit('register_room', roomId)
  }

  joinSession(sessionId: string): void {
    this.socket?.emit('join_session', sessionId)
  }

  notifyCallAccepted(sessionId: string, userId: string): void {
    const event: CallAcceptedEvent = { type: 'call_accepted', sessionId, userId }
    this.socket?.emit('call_accepted', event)
  }

  notifyCallDeclined(sessionId: string, userId: string, reason?: string): void {
    const event: CallDeclinedEvent = { type: 'call_declined', sessionId, userId, reason }
    this.socket?.emit('call_declined', event)
  }

  notifyCallEnded(sessionId: string, endedBy: string, reason?: string): void {
    const event: CallEndedEvent = { type: 'call_ended', sessionId, endedBy, reason }
    this.socket?.emit('call_ended', event)
  }

  /**
   * Generic method to emit custom events to backend
   * Used for media diagnostics and other custom events
   */
  emitToBackend(event: string, data: any): void {
    console.log(`[Frontend SignalingService] 🚀 Emitting '${event}' event to backend`)
    console.log('[Frontend SignalingService] Socket connected:', !!this.socket, 'Socket ID:', this.socket?.id)
    console.log('[Frontend SignalingService] Event data:', JSON.stringify(data, null, 2))
    
    if (!this.socket) {
      console.error('[Frontend SignalingService] ❌ Cannot emit - socket is not connected!')
      return
    }
    
    this.socket.emit(event, data)
    console.log(`[Frontend SignalingService] ✅ '${event}' event emitted to backend`)
  }
}

export const signalingService = new SignalingService()
